import { payResources } from "../resources/resource-pool";
import { getCardInstance, getCommanderDefinitionForInstance } from "../selectors/card-instances";
import { calculateEventLimit } from "../selectors/event-limit";
import { isGarrisonEmpty } from "../selectors/formation";
import type {
  DeployCommanderAction,
  GameEvent,
  GameStateV3,
  GameStateV4,
  GameStateV5,
} from "../types";
import type { TransitionResult } from "./advance-phase";

function eventId(actionSequence: number, ordinal: number): string {
  return `event-${actionSequence}-${ordinal}`;
}

export function applyDeployCommander(
  state: Readonly<GameStateV3 | GameStateV4 | GameStateV5>,
  action: DeployCommanderAction,
  actionSequence: number,
): TransitionResult {
  const instance = getCardInstance(state, action.commanderCardInstanceId);
  const definition = getCommanderDefinitionForInstance(state, action.commanderCardInstanceId);
  if (instance === undefined || definition === undefined) {
    throw new Error("Validated Commander deployment lost its schema-3 card definition.");
  }

  const player = state.players[action.playerId];
  const lane = state.lanes[action.laneId];
  const side = lane.players[action.playerId];
  const mayAttackOnDeploymentTurn =
    state.schemaVersion === 4 || state.schemaVersion === 5 ? !isGarrisonEmpty(side) : false;
  const previousKingdomPool = { ...player.kingdomPool };
  const costPaid = { ...definition.cost };
  const remainingKingdomPool = payResources(previousKingdomPool, costPaid);
  const eventLimitBefore = calculateEventLimit(state, action.playerId);
  const nextState: GameStateV3 | GameStateV4 | GameStateV5 = {
    ...state,
    players: {
      ...state.players,
      [action.playerId]: {
        ...player,
        hand: player.hand.filter(
          (cardInstanceId) => cardInstanceId !== action.commanderCardInstanceId,
        ),
        kingdomPool: remainingKingdomPool,
        actionsUsed: {
          ...player.actionsUsed,
          deploymentActions: 1,
        },
      },
    },
    lanes: {
      ...state.lanes,
      [action.laneId]: {
        ...lane,
        players: {
          ...lane.players,
          [action.playerId]: {
            ...side,
            commander: {
              cardInstanceId: action.commanderCardInstanceId,
              attack: definition.attack,
              guard: definition.guard,
              ...("armyCapacity" in definition ? { armyCapacity: definition.armyCapacity } : {}),
              readiness: "ready",
              deployedThisTurn: true,
              mayAttackOnDeploymentTurn,
            },
          },
        },
      },
    },
  };
  const garrisonArmyCardInstanceIds = side.armies.map((army) => army.cardInstanceId);
  const events: GameEvent[] = [
    {
      type: "kingdom-pool-spent",
      eventId: eventId(actionSequence, 0),
      actionSequence,
      playerId: action.playerId,
      laneId: action.laneId,
      commanderCardInstanceId: action.commanderCardInstanceId,
      cardDefinitionId: instance.cardDefinitionId,
      reason: "deploy-commander",
      costPaid,
      previousKingdomPool,
      remainingKingdomPool,
    },
    {
      type: "commander-deployed",
      eventId: eventId(actionSequence, 1),
      actionSequence,
      playerId: action.playerId,
      laneId: action.laneId,
      commanderCardInstanceId: action.commanderCardInstanceId,
      cardDefinitionId: instance.cardDefinitionId,
      printedAttack: definition.attack,
      printedGuard: definition.guard,
      ...("armyCapacity" in definition ? { armyCapacity: definition.armyCapacity } : {}),
      costPaid,
      remainingKingdomPool,
      garrisonArmyCardInstanceIds,
      eventLimitBefore,
      eventLimitAfter: calculateEventLimit(nextState, action.playerId),
    },
    {
      type: "deployment-action-used",
      eventId: eventId(actionSequence, 2),
      actionSequence,
      playerId: action.playerId,
      actionType: "deploy-commander",
      deploymentActionsUsed: 1,
    },
  ];

  return { state: nextState, events };
}
